/**
 * WOW · feel/measure background host (mounted once in App):
 *   - boots funnel analytics + Juno's reactions from the event stream;
 *   - "music director": which world's tune plays (route → world), paused in lessons unless the live
 *     config allows music there;
 *   - light haptic on every button tap (native only);
 *   - greets a child who comes back after a few days away;
 *   - renders Juno's reaction bubble.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import type { PathId } from '@sketchling/core';
import { IX } from '../../lib/content';
import { useApp } from '../../state/store';
import { CATEGORY_WORLD, worldOf } from '../../art/worlds';
import { flushEvents, startAnalytics, track } from '../../lib/analytics';
import { junoReact, maybeGreetComeback, startJunoReactions } from '../../lib/juno';
import { setMusicSuppressed, setMusicWorld } from '../../lib/music';
import { buzz } from '../../lib/haptics';
import { isNative } from '../../lib/platform';
import { JunoReactions } from './JunoReactions';
import { useWorldNow } from './worldNow';
import { presentReceipt } from './presenter';
import { putImage } from '../../lib/idb';

export { useWorldNow };

export function worldForRoute(pathname: string, currentPath: PathId): PathId {
  const lesson = /^\/lesson\/([^/?#]+)/.exec(pathname);
  if (lesson) {
    const id = decodeURIComponent(lesson[1]!);
    const node = IX.pathNodes.get(id);
    if (node) return node.path.id;
    const mu = IX.miniUnits.get(id);
    const d = mu ? IX.drawings.get(mu.drawingId) : undefined;
    return d ? worldOf(d) : currentPath;
  }
  // Exhibition rooms play their own world's music.
  const ex = /^\/exhibition\/([^/?#]+)/.exec(pathname);
  if (ex && IX.paths.has(ex[1] as PathId)) return ex[1] as PathId;
  const pg = /^\/playground\/([^/?#]+)/.exec(pathname);
  if (pg) return (CATEGORY_WORLD[pg[1]!] as PathId | undefined) ?? currentPath;
  return currentPath;
}

// Dev / e2e handle (never in production builds).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __skWow: unknown }).__skWow = { junoReact, track, flushEvents, presentReceipt, useApp, putImage };
}

const MAIN = /^\/(learn|playground|journal|studio|sparks|skills|challenges|letters|tales)/;

export function WowHost() {
  const loc = useLocation();
  const currentPath = useApp((s) => (s.activeProfileId ? (s.progress[s.activeProfileId]?.currentPath ?? 'intro') : 'intro'));
  const musicInLessons = useApp((s) => s.config.musicInLessons);
  const activeId = useApp((s) => s.activeProfileId);

  useEffect(() => {
    const a = startAnalytics();
    const j = startJunoReactions();
    return () => {
      a();
      j();
    };
  }, []);

  // Music director.
  useEffect(() => {
    const w = worldForRoute(loc.pathname, currentPath);
    useWorldNow.setState({ world: w });
    setMusicWorld(w);
    const inLesson = loc.pathname.startsWith('/lesson/') || loc.pathname.startsWith('/studio/flow') || loc.pathname.startsWith('/parents');
    setMusicSuppressed(inLesson && !musicInLessons);
  }, [loc.pathname, currentPath, musicInLessons]);

  // Comeback greeting on the first main screen of a launch.
  useEffect(() => {
    if (!MAIN.test(loc.pathname)) return;
    const t = setTimeout(() => maybeGreetComeback(), 1400);
    return () => clearTimeout(t);
  }, [loc.pathname, activeId]);

  // Native: a light tap under every button press.
  useEffect(() => {
    if (!isNative) return;
    const onDown = (e: PointerEvent) => {
      const el = (e.target as Element | null)?.closest?.('button, [role="button"], a.btn, .btn');
      if (!el || (el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') return;
      buzz('tap');
    };
    document.addEventListener('pointerdown', onDown, { capture: true, passive: true });
    return () => document.removeEventListener('pointerdown', onDown, { capture: true });
  }, []);

  return <JunoReactions />;
}
